<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Reporte de movimientos de stock</h1>
          <p class="view-header__subtitle">Todos los movimientos de stock, filtrables por producto, marca, depósito, fecha y tipo.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-funnel"></i>
          Filtros
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <q-select v-model="filters.provider_id" :options="providerFilterOptions" placeholder="- Todos los Proveedores -" searchable />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="filters.brand_id" :options="brandFilterOptions" placeholder="- Todas las Marcas -" searchable />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="filters.category_id" :options="categoryOptions" placeholder="- Todas las Categorías -" searchable />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="filters.qwarehouse" :options="warehouseOptions" placeholder="- Todos los Depósitos -" searchable />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="movement_type" :options="movementTypeOptions" />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="filters.active" :options="activeFilterOptions" />
          </div>
          <div class="filters-panel__field filters-panel__field--inline text-left">
            <label class="control-label">Fecha:</label>
            <Daterange
              :key="daterangeKey"
              :opendir="'left'"
              :filtered="true"
              @datepicked="dateUpdated"
            />
          </div>
        </div>
      </div>

      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscadores
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="filters.qcode" type="search" class="form-control" placeholder="Buscar codigo interno...">
          </div>
          <div class="filters-panel__field">
            <input v-model="filters.qbarcode" type="search" class="form-control" placeholder="Buscar codigo de barra...">
          </div>
          <div class="filters-panel__field">
            <input v-model="filters.qmanufacturercode" type="search" class="form-control" placeholder="Buscar codigo de fabrica...">
          </div>
          <div class="filters-panel__field">
            <input v-model="filters.qname" type="search" class="form-control" placeholder="Buscar nombre...">
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters()">
              <i class="bi bi-eraser"></i>
              Limpiar todo
            </button>
            <button type="button" class="filters-panel__btn" @click="exportPDF()">
              <i class="bi bi-filetype-pdf"></i>
              Exportar PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
      />
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import Daterange from "../global/Daterange-component.vue";
import QSelect from '@/components/shared/QSelect.vue';
import exportsMixin from "../../mixins/exportsMixin";
import { mapState } from "vuex";

export default {
  mixins: [exportsMixin],
  components: {
    VdtnetTable,
    Daterange,
    QSelect,
  },
  data: function () {
    return {
      isLoading: false,
      suppressReload: false,
      // Tope de registros para exportar a PDF. Mas que esto el servicio de PDF no
      // lo soporta bien (hay usuarios con cientos de miles de movimientos).
      exportLimit: 5000,
      // Se incrementa para forzar el re-montado del Daterange y devolverlo a su
      // valor por defecto (el picker maneja su propio estado interno).
      daterangeKey: 0,
      movement_type: "all",
      daterange: {
        fromDate: null,
        toDate: null,
      },
      filters: {
        qname: "",
        qcode: "",
        qbarcode: "",
        qmanufacturercode: "",
        qwarehouse: null,
        page: 1,
        itemsPerPage: 15,
        active: 1,
        provider_id: null,
        brand_id: null,
        category_id: null,
        sorting: {
          col: "",
          order: "desc",
        },
      },
      opts: {
        language: {
          url: "//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json",
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/movements/list",
          type: "GET",
          data: (d) => {
            d.movement_type = this.movement_type;
            d.daterange = this.daterange;
            d.warehouse_id = this.filters.qwarehouse || 0;
            d.filters = this.filters;
            return d.data;
          },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: true,
        pageLength: 15,
        searching: true,
        searchDelay: 1500,
        destroy: true,
        ordering: true,
        order: [[0, "desc"]],
        lengthChange: true,
        serverSide: true,
        fixedHeader: true,
        autoWidth: false,
      },
      fields: {
        created_at: {
          label: "Fecha y hora",
          defaultOrder: "desc",
          render: (data) => (data ? this.moment.utc(data).local().format("DD/MM/YYYY HH:mm:ss") : ""),
        },
        // Columnas calculadas en el backend (addColumn): no son columnas reales de
        // la tabla stock, asi que no se pueden ordenar en DB. orderable:false evita
        // full scans / errores al intentar ordenar por ellas con muchos registros.
        code: {
          label: "Codigo Int.",
          orderable: false,
          render: (data, type, row) => {
            const code = data
              ? `<span class="data-table__primary" title="Codigo Interno"><i class="bi bi-hash"></i>${data}</span>`
              : "";
            const barcode = row.barcode
              ? `<small class="data-table__muted" title="Codigo de barra"><i class="bi bi-upc-scan mr-1"></i>${row.barcode}</small>`
              : "";
            const mfr = row.manufacturer_code
              ? `<small class="data-table__muted" title="Codigo de fabrica"><i class="bi bi-box-seam mr-1"></i>${row.manufacturer_code}</small>`
              : "";
            return `<div class="d-flex flex-column">${code}${barcode}${mfr}</div>`;
          },
        },
        product: { label: "Producto", orderable: false },
        reference_id: { label: "Tipo", orderable: false },
        mov: {
          label: "Movimiento",
          orderable: false,
          render: (data) => {
            const mod = data === "Ingreso" ? "status-badge--success" : "status-badge--danger";
            return `<span class="status-badge ${mod}">${data || ""}</span>`;
          },
        },
        quantity: { label: "Cantidad" },
        warehouse: { label: "Deposito", orderable: false },
      },
    };
  },
  computed: {
    ...mapState("categories", { categories: (state) => state.categories }),
    ...mapState("warehouse", { warehouse: (state) => state.warehouse }),
    ...mapState("brands", { brands: (state) => state.brands }),
    ...mapState("providers", { providers: (state) => state.providers }),
    providerFilterOptions() {
      return [{ value: 'Sin proveedor', label: 'Sin Proveedor' }, ...this.providers.map(provider => ({ value: provider.id, label: provider.name }))];
    },
    brandFilterOptions() {
      return [{ value: 'Sin marca', label: 'Sin Marca' }, ...this.brands.map(brand => ({ value: brand.id, label: brand.name }))];
    },
    categoryOptions() {
      return this.categories.map(category => ({ value: category.id, label: category.name }));
    },
    warehouseOptions() {
      return (Array.isArray(this.warehouse) ? this.warehouse : []).map(w => ({ value: w.id, label: w.name }));
    },
    movementTypeOptions() {
      return [
        { value: 'all', label: 'Todos los movimientos' },
        { value: 'in', label: 'Entrada' },
        { value: 'out', label: 'Salida' },
      ];
    },
    activeFilterOptions() {
      return [
        { value: 0, label: 'Todos los productos' },
        { value: 1, label: 'Productos activos' },
        { value: 2, label: 'Productos inactivos' },
      ];
    },
  },
  created() {
    this.$store.dispatch("categories/getCategories");
    this.$store.dispatch("warehouse/getWarehouses");
    this.$store.dispatch("brands/getBrands");
    this.$store.dispatch("providers/getProviders");
  },
  mounted() {
    this.setDefaultDateRange();
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on("preXhr.dt", () => { this.isLoading = true; });
      dt.on("xhr.dt", () => { this.isLoading = false; });
    });
  },
  watch: {
    "filters.qwarehouse": { handler: function () { this.refreshTable(); } },
    "filters.category_id": { handler: function () { this.refreshTable(); } },
    "filters.brand_id": { handler: function () { this.refreshTable(); } },
    "filters.provider_id": { handler: function () { this.refreshTable(); } },
    "filters.active": { handler: function () { this.refreshTable(); } },
    "filters.qname": { handler: function () { this.debouncedRefresh(); } },
    "filters.qcode": { handler: function () { this.debouncedRefresh(); } },
    "filters.qbarcode": { handler: function () { this.debouncedRefresh(); } },
    "filters.qmanufacturercode": { handler: function () { this.debouncedRefresh(); } },
    movement_type() { this.refreshTable(); },
  },
  methods: {
    refreshTable() {
      if (this.suppressReload) return;
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    debouncedRefresh() {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this.refreshTable(), 500);
    },
    dateUpdated(date) {
      this.daterange.fromDate = date.startDate;
      this.daterange.toDate = date.endDate;
      this.refreshTable();
    },
    // Conteo de movimientos que matchean el filtro actual, tomado de la ultima
    // carga de la tabla (recordsDisplay). Devuelve null si no esta disponible.
    filteredCount() {
      try {
        const dt = this.$refs.table && this.$refs.table.dataTable;
        if (!dt || typeof dt.page !== "function") return null;
        const info = dt.page.info();
        return info ? info.recordsDisplay : null;
      } catch (e) {
        return null;
      }
    },
    // El PDF se genera async en el backend (mismos filtros que la tabla) y queda
    // disponible en la pantalla "Descargas". No devuelve el archivo directo.
    exportPDF() {
      // 1) Exigir rango de fechas: sin fechas (ej. "Todas") el export podria
      //    arrastrar cientos de miles de movimientos.
      if (!this.daterange.fromDate || !this.daterange.toDate) {
        alert("Para exportar el PDF necesitás seleccionar un rango de fechas.");
        return;
      }
      // 2) Tope de registros: reutilizamos el conteo filtrado que la tabla ya
      //    calculo en su ultima carga, asi frenamos antes de pegarle al backend.
      const total = this.filteredCount();
      if (total !== null && total > this.exportLimit) {
        alert(
          `Hay ${total.toLocaleString("es-AR")} movimientos para estos filtros. ` +
          `El máximo para exportar es ${this.exportLimit.toLocaleString("es-AR")}. ` +
          `Acotá por fecha, producto, marca o depósito.`
        );
        return;
      }
      const payload = {
        movement_type: this.movement_type,
        daterange: this.daterange,
        warehouse_id: this.filters.qwarehouse || 0,
        filters: this.filters,
      };
      this.exportToDescargas(
        "/api/reports/PDFStockMovementsReport",
        payload,
        "Movimientos de stock (PDF)"
      ).catch((error) => {
        const serverMsg = error.response && error.response.data && error.response.data.message;
        console.error("exportPDF:", serverMsg || error);
      });
    },
    clearFilters() {
      clearTimeout(this._refreshTimer);
      this.suppressReload = true;
      this.filters.qname = "";
      this.filters.qcode = "";
      this.filters.qbarcode = "";
      this.filters.qmanufacturercode = "";
      this.filters.qwarehouse = null;
      this.filters.provider_id = null;
      this.filters.brand_id = null;
      this.filters.category_id = null;
      this.filters.active = 1;
      this.movement_type = "all";
      // Resetea la fecha al rango por defecto (ultimos 30 dias) en data y en el
      // picker (via key), para que "Limpiar todo" realmente limpie todo.
      this.setDefaultDateRange();
      this.daterangeKey++;
      this.$nextTick(() => {
        this.suppressReload = false;
        this.refreshTable();
      });
    },
    setDefaultDateRange() {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 30);
      this.daterange.fromDate = this.formatDate(startDate);
      this.daterange.toDate = this.formatDate(today);
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
